
import type React from "react"
import { createContext, useContext, useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { selectLanguage } from "../features/language/languageSlice"
import { fetchSettings } from "../api/settings"
import brandManager from "../services/brandManager"

interface ThemeContextType {
  primaryColor: string
  secondaryColor: string
  headerLogo: string
  favIcon: string
  restaurantName: string
  canOrder: boolean
  isLoading: boolean
}

const defaultTheme: ThemeContextType = {
  primaryColor: brandManager.currentPrimaryColor,
  secondaryColor: brandManager.currentSecondaryColor,
  headerLogo: brandManager.currentLogoUrl,
  favIcon: "/logo.svg",
  restaurantName: brandManager.currentAppName,
  canOrder: true,
  isLoading: true,
}

const ThemeContext = createContext<ThemeContextType>(defaultTheme)

export const useTheme = () => useContext(ThemeContext)

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<ThemeContextType>(defaultTheme)
  const currentLanguage = useSelector(selectLanguage)

  useEffect(() => {
    let isMounted = true // Flag to prevent setting state after unmount
    
    const loadSettings = async () => {
      try {
        const settings = await fetchSettings()
        
        if (isMounted) {
          setTheme({
            primaryColor: settings.primary_color || brandManager.currentPrimaryColor,
            secondaryColor: settings.secondary_color || brandManager.currentSecondaryColor,
            headerLogo: settings.header_logo || brandManager.currentLogoUrl,
            favIcon: settings.fav_icon || defaultTheme.favIcon,
            restaurantName: settings.restaurant_name || brandManager.currentAppName,
            canOrder: settings.canOrder,
            isLoading: false,
          })
        }
      } catch (error) {
        if (isMounted) {
          setTheme({ ...defaultTheme, isLoading: false })
        }
      }
    }

    // Add a small delay to prevent rapid successive calls
    const timeoutId = setTimeout(loadSettings, 50)
    
    return () => {
      isMounted = false
      clearTimeout(timeoutId)
    }
  }, [currentLanguage]) // Re-fetch when language changes

  return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>
}
